<template>
  <div :class="fillHeight ? 'flex flex-col h-full' : 'flex flex-col'">
    <div v-if="!hasData" class="flex items-center justify-center h-full">
      <p class="text-sm text-gray-600">ไม่มีข้อมูล</p>
    </div>
    <template v-else>
      <div :class="fillHeight ? 'overflow-x-auto flex-1 min-h-0' : 'overflow-x-auto'">
        <div :style="innerStyle" :class="fillHeight ? 'h-full' : ''">
          <div
            ref="chartRef"
            :id="chartId"
            data-chart-export
            :class="fillHeight ? 'h-full w-full' : 'w-full'"
            :style="!fillHeight ? { aspectRatio: String(aspectRatioValue) } : {}"
          />
        </div>
      </div>

      <div v-if="showLegend && legendItems.length > 1" class="flex flex-wrap justify-center gap-x-5 gap-y-1.5 mt-2 px-0.5">
        <button
          v-for="item in legendItems"
          :key="item.datasetIndex"
          type="button"
          class="flex items-center gap-2 text-xs leading-none cursor-pointer select-none"
          :class="{ 'opacity-40': item.hidden }"
          @click="toggleDataset(item)"
        >
          <span
            v-if="item.pointStyle === 'line'"
            class="inline-block w-7 h-0 flex-shrink-0"
            :style="{
              borderTopWidth: '2px',
              borderTopStyle: item.lineDash && item.lineDash.length ? 'dashed' : 'solid',
              borderTopColor: item.strokeStyle || '#000'
            }"
          />
          <span
            v-else
            class="inline-block w-7 h-2 rounded-sm flex-shrink-0"
            :style="{ backgroundColor: item.fillStyle || '#000' }"
          />
          <span :class="{ 'line-through': item.hidden }">{{ item.text }}</span>
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import * as echarts from 'echarts'
import { helper as $h } from '@/utils/helper'
import { globalNumDecFixed } from '@/utils/format-number'


const props = defineProps({
  chartId: { type: String, default: 'dashboardChart' },
  type: { type: String, default: 'bar' },
  data: { type: Object, required: true, default: () => ({}) },
  options: { type: Object, default: () => ({}) },
  plugins: { type: [Object, Array], default: () => [] },
  minWidth: { type: String, default: '' },
  unit: { type: String, default: '' },
  showLegend: { type: Boolean, default: true },
  fillHeight: { type: Boolean, default: false },
})

const chartRef = ref()
const legendItems = ref([])
let chart = null
let resizeObserver = null
let currentRawData = { labels: [], datasets: [] }
const hiddenIndices = reactive(new Set())

const average = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0)

const effectiveMinWidth = computed(() => {
  if (props.minWidth) return props.minWidth
  // Horizontal charts (indexAxis:'y') don't need x-scroll
  if (props.options?.indexAxis === 'y') return ''
  const labelCount = props.data?.labels?.length || 0
  if (!labelCount) return ''
  // Wider per-category slot than before (60 -> 90px) so long/rotated x-axis labels have room
  // to render without overlapping their neighbors - barCategoryGap alone doesn't do this since
  // it only changes how much of a category's band the bars occupy, not the band width itself.
  const px = labelCount * 90 + 60
  return px > 300 ? `${px}px` : ''
})

const innerStyle = computed(() => {
  const s = {}
  if (effectiveMinWidth.value) s.minWidth = effectiveMinWidth.value
  return s
})

const aspectRatioValue = computed(() => props.options?.aspectRatio || 2)

const hasData = computed(() => {
  const raw = props.data
  return !!(raw?.datasets?.length && raw.datasets.some((ds) => (ds.data || []).some((v) => v !== null && v !== undefined && Number(v) !== 0)))
})

const buildData = () => {
  const raw = $h.toRaw(props.data)
  if (!raw?.labels?.length || !raw?.datasets?.length) return raw
  const barDatasets = raw.datasets.filter((ds) => ds.type !== 'line')
  const checkDatasets = barDatasets.length > 0 ? barDatasets : raw.datasets
  const keepIndices = raw.labels
    .map((_, i) => i)
    .filter((i) => checkDatasets.some((ds) => {
      const v = ds.data[i]
      return v !== null && v !== undefined && Number(v) !== 0
    }))
  if (keepIndices.length === raw.labels.length) return raw
  return {
    ...raw,
    labels: keepIndices.map((i) => raw.labels[i]),
    datasets: raw.datasets.map((ds) => ({
      ...ds,
      data: keepIndices.map((i) => ds.data[i]),
    })),
  }
}

const buildLegendItems = (rawData) => {
  const opts = props.options || {}
  const labelsCfg = opts.plugins?.legend?.labels
  const datasets = rawData?.datasets || []
  if (labelsCfg?.generateLabels) {
    const fakeChart = {
      data: { datasets },
      isDatasetVisible: (i) => !hiddenIndices.has(i),
    }
    return labelsCfg.generateLabels(fakeChart).map((item) => ({ ...item }))
  }
  return datasets.map((ds, i) => ({
    text: ds.label,
    datasetIndex: i,
    hidden: hiddenIndices.has(i),
    fillStyle: ds.backgroundColor,
    strokeStyle: ds.borderColor || ds.backgroundColor,
    lineWidth: ds.type === 'line' ? (ds.borderWidth || 1.5) : 0,
    lineDash: ds.borderDash || [],
    pointStyle: ds.type === 'line' ? 'line' : 'rect',
  }))
}

const buildTooltipText = (p, rawData, opts, isHorizontal) => {
  if (!p || p.seriesType !== 'bar' || p.value === null || p.value === undefined) return ''
  const cb = opts.plugins?.tooltip?.callbacks?.label
  const ds = rawData.datasets.find((d) => d.label === p.seriesName) || {}
  let bodyText
  if (cb) {
    const ctx = {
      dataset: ds,
      datasetIndex: rawData.datasets.indexOf(ds),
      dataIndex: p.dataIndex,
      parsed: isHorizontal ? { x: p.value, y: p.dataIndex } : { x: p.dataIndex, y: p.value },
      chart: { data: rawData },
    }
    bodyText = cb(ctx)
  } else {
    const val = globalNumDecFixed(p.value)
    bodyText = props.unit ? `${p.seriesName}: ${val} ${props.unit}` : `${p.seriesName}: ${val}`
  }
  const axisLabel = String(p.name ?? '').replace(/\n/g, ', ')

  const marker = `<span style="display:inline-block;margin-right:5px;width:10px;height:10px;background-color:${p.color};border:1px solid #fff;"></span>`
  return `<div style="position:relative;"><div style="font-weight:600;margin-bottom:4px;">${axisLabel}</div><div>${marker}${bodyText}</div></div>`
}

const buildOption = (rawData) => {
  const opts = props.options || {}
  const isHorizontal = opts.indexAxis === 'y'
  const catAxisCfg = isHorizontal ? opts.scales?.y : opts.scales?.x
  const valAxisCfg = isHorizontal ? opts.scales?.x : opts.scales?.y

  const categories = (rawData.labels || []).map((l) => (Array.isArray(l) ? l.join('\n') : l))
  const allDatasets = rawData.datasets || []
  const thresholdDs = allDatasets.find((ds) => ds.type === 'line')
  const barDatasetsAll = allDatasets.filter((ds) => ds.type !== 'line')
  const stacked = !!(opts.scales?.x?.stacked || opts.scales?.y?.stacked)
  const dlCfg = opts.plugins?.datalabels

  const avgCategoryPct = average(barDatasetsAll.map((ds) => ds.categoryPercentage ?? 0.8))
  const avgBarPct = average(barDatasetsAll.map((ds) => ds.barPercentage ?? 0.9))

  const thresholdHidden = thresholdDs ? hiddenIndices.has(allDatasets.indexOf(thresholdDs)) : true

  const buildMarkLine = () => {
    if (!thresholdDs || thresholdHidden) return undefined
    const value = thresholdDs.data?.[0]
    if (value === null || value === undefined) return undefined
    const color = thresholdDs.borderColor || thresholdDs.backgroundColor || '#3A506A'
    return {
      silent: true,
      symbol: ['none', 'arrow'],
      symbolSize: 8,
      animation: false,
      lineStyle: { color, type: 'dashed', width: 1.5 },
      label: {
        show: true,
        formatter: () => [thresholdDs.label, globalNumDecFixed(value), props.unit].filter(Boolean).join('\n'),
        color,
        fontFamily: 'Kanit',
        fontSize: 12,
        position: 'end',
        align: 'left',
      },
      data: [{ [isHorizontal ? 'xAxis' : 'yAxis']: value }],
    }
  }

  let seriesIndex = 0
  const series = barDatasetsAll.map((ds) => {
    const dsIndex = allDatasets.indexOf(ds)
    const hidden = hiddenIndices.has(dsIndex)
    const isFirstVisible = !hidden && seriesIndex === 0
    if (!hidden) seriesIndex += 1
    const perBarColors = Array.isArray(ds.backgroundColor) ? ds.backgroundColor : null
    return {
      name: ds.label,
      type: 'bar',
      data: hidden
        ? categories.map(() => null)
        : (perBarColors ? ds.data.map((v, i) => ({ value: v, itemStyle: { color: perBarColors[i] } })) : ds.data),
      stack: stacked ? 'total' : undefined,
      barMaxWidth: 48,
      barCategoryGap: isFirstVisible ? `${Math.max(30, Math.round((1 - avgCategoryPct) * 100))}%` : undefined,
      barGap: isFirstVisible ? `${Math.round(((1 - avgBarPct) / Math.max(avgBarPct, 0.01)) * 60)}%` : undefined,
      itemStyle: (ds.backgroundColor && !perBarColors) ? { color: ds.backgroundColor } : undefined,
      label: dlCfg && !hidden ? {
        show: true,
        position: isHorizontal ? 'inside' : 'top',
        formatter: (p) => {
          if (!dlCfg.formatter) return p.value
          const text = dlCfg.formatter(p.value, { dataIndex: p.dataIndex, datasetIndex: dsIndex, dataset: ds })
          return text === null || text === undefined ? '' : text
        },
        fontFamily: dlCfg.font?.family || 'Kanit',
        fontSize: dlCfg.font?.size || 12,
        color: dlCfg.color || '#374151',
      } : undefined,
      markLine: isFirstVisible ? buildMarkLine() : undefined,
    }
  })

  const categoryAxis = {
    type: 'category',
    data: categories,
    show: catAxisCfg?.display !== false,
    axisLabel: {
      rotate: isHorizontal ? 0 : (catAxisCfg?.ticks?.maxRotation ?? 0),
      fontFamily: catAxisCfg?.ticks?.font?.family || 'Kanit',
      fontSize: catAxisCfg?.ticks?.font?.size || 12,
      interval: catAxisCfg?.ticks?.autoSkip === false ? 0 : 'auto',
      // Chart.js category-scale tick callbacks are called as `this.getLabelForValue(index)` with
      // the tick's numeric index as `value` (e.g. wrapLabelOnNarrowChart in NeetsPanel.vue) -
      // reuse them unmodified via a minimal scale-context shim rather than rewriting them.
      formatter: catAxisCfg?.ticks?.callback
        ? (_label, index) => catAxisCfg.ticks.callback.call(
          { getLabelForValue: (i) => categories[i], chart: { width: chartRef.value?.clientWidth || 9999 } },
          index,
          index,
        )
        : undefined,
      hideOverlap: false,
      margin: 14,
    },
    axisLine: { show: catAxisCfg?.display !== false },
    axisTick: { show: catAxisCfg?.display !== false, alignWithLabel: true },
    splitLine: { show: !!catAxisCfg?.grid?.display },
    // No axisPointer shadow - with trigger:'item' hovering the category area (not a bar
    // itself) has no item to report, which showed a stray "undefined" tooltip alongside
    // the shadow. Only hovering an actual bar should highlight/show anything.
    axisPointer: { show: false },
  }

  const valueAxis = {
    type: 'value',
    show: valAxisCfg?.display !== false,
    min: valAxisCfg?.beginAtZero ? 0 : undefined,
    max: valAxisCfg?.max,
    minInterval: valAxisCfg?.ticks?.precision === 0 ? 1 : undefined,
    name: valAxisCfg?.title?.display ? valAxisCfg.title.text : undefined,
    nameLocation: 'middle',
    nameGap: 40,
    nameTextStyle: { fontFamily: 'Kanit', fontSize: 12 },
    axisLine: { show: valAxisCfg?.display !== false },
    axisLabel: {
      fontFamily: valAxisCfg?.ticks?.font?.family || 'Kanit',
      fontSize: valAxisCfg?.ticks?.font?.size || 12,
      formatter: valAxisCfg?.ticks?.callback
        ? (v) => valAxisCfg.ticks.callback(v)
        : (valAxisCfg?.ticks?.precision === 0 ? (v) => Math.round(v) : undefined),
    },
    splitLine: {
      show: valAxisCfg?.grid?.display !== false,
      lineStyle: { color: valAxisCfg?.grid?.color || '#E2E8F0' },
    },
  }

  return {
    textStyle: { fontFamily: 'Kanit' },
    animation: true,
    grid: {
      left: 20,
      right: opts.layout?.padding?.right || 16,
      top: opts.layout?.padding?.top || 24,
      bottom: opts.layout?.padding?.bottom || 8,
      containLabel: true,
    },
    legend: { show: false },
    tooltip: {
      show: opts.plugins?.tooltip?.enabled !== false,
      trigger: 'item',
      backgroundColor: 'rgba(0,0,0,0.75)',
      borderWidth: 0,
      padding: [8, 12],
      extraCssText: 'border-radius:6px;overflow:visible;',
      textStyle: { fontFamily: 'Kanit', fontSize: 12, color: '#fff' },
      formatter: (params) => buildTooltipText(params, rawData, opts, isHorizontal),
    },
    xAxis: isHorizontal ? valueAxis : categoryAxis,
    yAxis: isHorizontal ? categoryAxis : valueAxis,
    series,
  }
}

const renderChart = () => {
  currentRawData = buildData()
  legendItems.value = buildLegendItems(currentRawData)
  if (!chart) return
  chart.setOption(buildOption(currentRawData), true)
}

const initChart = () => {
  if (chart || !chartRef.value) return
  // SVG renderer (not canvas) - Safari mispositions canvas-rotated tick labels for the same
  // primitives Chart.js uses; SVG rotates a <text> element via a CSS transform instead, which
  // renders consistently across Chrome and Safari.
  chart = echarts.init(chartRef.value, null, { renderer: 'svg' })
  renderChart()
  resizeObserver = new ResizeObserver(() => chart?.resize())
  resizeObserver.observe(chartRef.value)
}

const destroyChart = () => {
  resizeObserver?.disconnect()
  resizeObserver = null
  chart?.dispose()
  chart = null
}

const toggleDataset = (item) => {
  const idx = item.datasetIndex
  if (hiddenIndices.has(idx)) hiddenIndices.delete(idx)
  else hiddenIndices.add(idx)
  renderChart()
}

watch(() => [props.data, props.options, props.unit], () => {
  if (!chart) return
  renderChart()
}, { deep: true })

watch(hasData, async (val) => {
  if (!val) {
    destroyChart()
    return
  }
  await nextTick()
  initChart()
})

onMounted(() => {
  if (hasData.value) initChart()
})

onUnmounted(() => {
  destroyChart()
})
</script>
